import "./Activity.css";
import { useNavigate } from "react-router-dom";
import LeftDrawer from "../components/drawers/LeftDrawer";
import BottomRightDrawer from "../components/drawers/BottomRightDrawer";
import UpperRightDrawer from "../components/drawers/UpperRightDrawer";
import { Document, Page, pdfjs } from "react-pdf";
import ActivityContainer from "../components/ActivityContainer";
import { useEffect, useState, useContext } from "react";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { noActivity } from "../data/noActivityList";
import { AuthContext } from "../Auth.js";
import {
  effectiveTier,
  canAccessActivity,
  activityNameForPdf,
} from "../data/tierAccess";
import AccessGateModal from "../components/AccessGateModal";
import { capitalize } from "../utils/capitalize";

// Serve the pdf.js worker from public/ (copied verbatim by the prebuild/prestart
// script) rather than letting webpack process it — CRA's Babel pass rewrites the
// worker's internal imports to absolute filesystem paths, which 404 in the browser.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

export default function Activity() {
  const { currentUser, authUserData } = useContext(AuthContext);
  const navigate = useNavigate();
  const [pdf, setPdf] = useState("/piggybackHomePage.pdf");
  const [pdf2, setPdf2] = useState("/HELPLandingPage.pdf");
  const [hide, setHide] = useState("block");
  const [LUPDF, setLUPDF] = useState(
    "/VisuallyDirectedFineMotor/OrigamiLU.pdf",
  );
  // null | { kind: "no-license" } | { kind: "activity", activity }
  const [gate, setGate] = useState(null);

  useEffect(() => {
    // Truly unauthenticated users must sign in first.
    if (!currentUser) {
      navigate("/login");
      return;
    }
    // Wait for the (lazily-created) user doc to resolve.
    if (authUserData === undefined || authUserData === null) {
      return;
    }
    // Logged in but no valid access grant (and not admin): show the gate modal
    // instead of silently bouncing to /login. hasAccess is admin OR a valid
    // grant, resolved server-side in Auth.js (claimAccess).
    if (!authUserData.hasAccess) {
      setGate({ kind: "no-license" });
      return;
    }
    // Has access: clear any stale no-access gate (e.g. just claimed a seat).
    setGate((g) => (g?.kind === "no-license" ? null : g));

    // Per-activity tier gating: when a locked activity is loaded (direct nav /
    // LU link), reset to the safe landing PDF and explain why with a modal.
    // Non-activity PDFs (landing/help pages) resolve to null and are never gated.
    const tier = effectiveTier(authUserData);
    const activityName = activityNameForPdf(pdf);
    if (activityName && !canAccessActivity(tier, activityName)) {
      setGate({ kind: "activity", activity: activityName });
      setPdf("/piggybackHomePage.pdf");
      return;
    }
    if (noActivity.includes(pdf)) {
      setHide("none");
    } else {
      setHide("block");
    }
  }, [pdf, currentUser, authUserData, navigate]);

  // Build the modal content for the current gate.
  const gateModal = (() => {
    if (!gate) {
      return { open: false };
    }
    const buy = { label: "View plans", onClick: () => navigate("/purchase"), primary: true };
    const account = { label: "Go to my account", onClick: () => navigate("/account") };
    const home = { label: "Back to home", onClick: () => navigate("/") };
    if (gate.kind === "no-license") {
      return {
        open: true,
        title: "Get access to play",
        message:
          "You need an active plan to access the activities. Choose a plan, " +
          "or ask your practice to invite your email to a seat.",
        actions: [buy, account, home],
        // Blocking: no onClose — the user must pick an action.
      };
    }
    // Locked activity for the current tier (today only Parents is restricted).
    // Name the user's actual plan rather than assuming it, so the copy stays
    // correct if the tier model grows.
    const currentTier = effectiveTier(authUserData);
    const planName = currentTier ? `${capitalize(currentTier)} plan` : "plan";
    return {
      open: true,
      title: "Not in your plan",
      requiredTier: "regular",
      message:
        `"${gate.activity}" isn't included in your ${planName}. Upgrade to ` +
        "Regular or Premium to unlock all activities.",
      actions: [buy],
      onClose: () => setGate(null),
    };
  })();

  return (
    <div>
      <AccessGateModal
        open={gateModal.open}
        title={gateModal.title}
        message={gateModal.message}
        requiredTier={gateModal.requiredTier}
        actions={gateModal.actions}
        onClose={gateModal.onClose}
      />
      <div id="header">
        <div
          onClick={() => navigate("/")}
          style={{
            cursor: "pointer",
            fontFamily: "NexaBold",
            fontSize: "26px",
          }}
        >
          Piggyback
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "1rem",
          }}
          onClick={() => navigate("/")}
        >
          <div
            style={{
              cursor: "pointer",
              fontFamily: "NexaBold",
              fontSize: "26px",
            }}
          >
            {"<"}
          </div>
          <div
            style={{
              cursor: "pointer",
              fontFamily: "NexaBold",
              fontSize: "26px",
            }}
          >
            Home
          </div>
        </div>
      </div>
      <div id="activitySection">
        <div style={{ width: "80px" }}>
          <LeftDrawer
            changePdf={setPdf}
            changePdf2={setPdf2}
            onLockedActivity={(activity) =>
              setGate({ kind: "activity", activity })
            }
          />
        </div>
        <div id="pdfView">
          <Document file={pdf}>
            <Page
              pageNumber={1}
              className="pageStyle"
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
        </div>
        <div style={{ width: "600px", display: hide }}>
          <ActivityContainer
            activity={pdf}
            changePdf={setPdf2}
            changeLU={setLUPDF}
          />
        </div>

        <div id="rightDrawerSection">
          <UpperRightDrawer pdf={pdf.includes("Origami") ? LUPDF : pdf} />
          <BottomRightDrawer pdf={pdf2} />
        </div>
      </div>
    </div>
  );
}
